import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_PROMPT_VARIABLES_QUERY_KEY } from './useGetPromptVariables';

import PromptVariablesService, {
  PromptVariable,
  CreatePromptVariableDto,
  FilterPromptVariablesDto,
} from '@/services/PromptsService';

export function useUpdatePromptVariable(filters: FilterPromptVariablesDto) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreatePromptVariableDto>;
    }) => {
      const result = await PromptVariablesService.updatePromptVariable(
        id,
        data,
      );
      return result;
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({
        queryKey: GET_PROMPT_VARIABLES_QUERY_KEY(filters),
      });

      const previousVariables = queryClient.getQueryData(
        GET_PROMPT_VARIABLES_QUERY_KEY(filters),
      );

      queryClient.setQueryData(
        GET_PROMPT_VARIABLES_QUERY_KEY(filters),
        (old: any) => {
          if (!old) return old;

          if (old.pages) {
            const newData = {
              ...old,
              pages: old.pages.map((page: any) => {
                return {
                  ...page,
                  items: page.items
                    ? page.items.map((variable: PromptVariable) => {
                        if (variable.id === id) {
                          const updatedVariable = {
                            ...variable,
                            ...data,
                            updatedAt: new Date().toISOString(),
                          };
                          return updatedVariable;
                        }
                        return variable;
                      })
                    : page.items,
                };
              }),
            };
            return newData;
          }

          return old;
        },
      );

      return { previousVariables };
    },
    onError: (_, __, context) => {
      if (context?.previousVariables) {
        queryClient.setQueryData(
          GET_PROMPT_VARIABLES_QUERY_KEY(filters),
          context.previousVariables,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: GET_PROMPT_VARIABLES_QUERY_KEY(filters),
      });
    },
  });

  return {
    updatePromptVariable: mutateAsync,
    isUpdating: isPending,
  };
}
